/**
 * Maps the Details step's form values to the API request shape.
 * Handles field renames, type coercions, mode-specific branches
 * (manual vs self-schedule), and strips UI-only fields.
 */
import type { ScheduleMode } from '../config/types';
import { isSelfScheduleMode } from '../config/modeClassifiers';
import { normalizeTimeZoneForAtsCalendarApi } from '@/api/calendarEvents';
import type { BusyEvent, InterviewKind, Interval } from '../availability';
import {
  scheduleTypeFor,
  type FindTimeValue,
} from '../calendar/selfSchedule/findTime/modes';
import type { SlotOverrideType } from '../calendar/selfSchedule/types';
import { computeSlotOverride } from './computeSlotOverride';
import type { ContextIds, BookedSlot, ScheduleSlot } from './types';

// UI-only field names the DetailsStep carries in `payload.details`
// that the wizard doesn't need to send to the API. `bookedSlot` and
// `selfScheduleSlots` are deliberately NOT in this list — they drive
// mapDetailsToApi's start_time/end_time/time_slots emission and must
// survive into that mapper. The UI-only keys below would otherwise
// be silently dropped by the backend permit list, but we strip them
// defensively so request bodies stay small + logs readable.
const UI_ONLY_FIELDS = [
  'dateTime',
  'findTime',
  'book_meeting_room',
  'candidatePhone',
  'candidate_phone',
  'isCandidatePhoneEditing',
  'isDateTimeEditing',
] as const;

// `_overrideContext` is also UI-only — but it's NOT in `UI_ONLY_FIELDS`
// because `buildPayload` calls `stripUiOnlyFields(details)` BEFORE
// `mapDetailsToApi(details)`. Putting it there would strip the snapshot
// before `mapDetailsToApi` could read it. Instead, `mapDetailsToApi`
// builds `mapped` key-by-key (no spread of `details`) so anything not
// explicitly copied — including `_overrideContext` — is naturally
// absent from the API output. The mapDetails-time tests assert this.

interface SelfScheduleOverrideContext {
  workingHoursByInterviewer: Record<string, Interval[]>;
  busyEvents: ReadonlyArray<BusyEvent>;
  interviewerIds: ReadonlyArray<number | string>;
  interviewKind: InterviewKind;
}

export function stripUiOnlyFields(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const cleaned = { ...obj };
  for (const key of UI_ONLY_FIELDS) {
    delete cleaned[key];
  }
  return cleaned;
}

export function applyContextIdsForMode(
  ids: ContextIds,
  mode: ScheduleMode
): Record<string, unknown> {
  const isSelfSchedule = isSelfScheduleMode(mode);
  // /ats_schedules accepts `contact_id` but NOT `candidate_id`; the
  // interview endpoint is the opposite. Rename here so the caller can
  // keep a single `candidate_id` field in the wizard's context IDs.
  if (isSelfSchedule && typeof ids.candidate_id === 'number') {
    const { candidate_id, ...rest } = ids;
    return { ...rest, contact_id: candidate_id };
  }
  return { ...ids };
}

export function mapDetailsToApi(
  details: Record<string, unknown>,
  mode: ScheduleMode
): Record<string, unknown> {
  const isSelfSchedule = isSelfScheduleMode(mode);

  const mapped: Record<string, unknown> = {};

  // Interview name: form uses `interviewName`, API uses `name`
  if ('interviewName' in details) {
    mapped.name = details.interviewName;
  }

  // Interview type field name diverges between endpoints:
  //   /ats_interviews — accepts `kind` (and also `interview_kind` for new
  //   callers); legacy call sites use `kind`.
  //   /ats_schedules — accepts ONLY `interview_kind` (see
  //   ats_schedules_helper.rb schedule_params_keys).
  // Send the right field per mode so self-schedule doesn't silently drop
  // the value.
  if ('interview_type' in details) {
    if (isSelfSchedule) {
      mapped.interview_kind = details.interview_type;
    } else {
      mapped.kind = details.interview_type;
    }
  }

  // Participant IDs: always send as number array
  if ('participant_ids' in details) {
    const raw = details.participant_ids;
    if (Array.isArray(raw)) {
      mapped.participant_ids = raw.map(Number);
    } else if (raw) {
      mapped.participant_ids = [Number(raw)];
    } else {
      mapped.participant_ids = [];
    }
  }

  // Duration — NOT in /ats_interviews permit list. Backend computes
  // it from (end_time - start_time) on the attendee record. For
  // /ats_schedules duration IS permitted, so forward it there.
  if (isSelfSchedule && 'duration' in details) {
    mapped.duration = Number(details.duration) || 30;
  }

  if ('location' in details) {
    mapped.location = details.location;
  }

  if (details.host_id) {
    mapped.host_id = Number(details.host_id);
  }

  if (details.ats_office_id) {
    mapped.ats_office_id = Number(details.ats_office_id);
  }

  // HackerRank template id — `/ats_interviews` permits this; the
  // `/ats_schedules` permit list does NOT include it (see
  // `core/app/controllers/concerns/ats/ats_schedules_helper.rb`
  // schedule_params_keys). Sending it on self-schedule was a silent
  // no-op — backend stripped it via the permit filter, the wizard
  // looked like it was wired but the field never landed. Gate by mode
  // so we don't ship dead bytes on every self-schedule save and so
  // anyone reading the audit trail sees a clean "self-schedule
  // doesn't carry ext_interview_template_id" signal.
  if (
    !isSelfSchedule &&
    details.location === 'hackerrank' &&
    details.ext_interview_template_id
  ) {
    mapped.ext_interview_template_id = details.ext_interview_template_id;
  }

  // Calendar visibility: form stores boolean, API expects string
  if ('calendar_visibility' in details) {
    mapped.calendar_visibility = details.calendar_visibility
      ? 'private'
      : 'default';
  }

  if ('enable_note_taker' in details) {
    mapped.note_taker_enabled = Boolean(details.enable_note_taker);
  }

  // Calendar info — only when plan allows shared calendars
  const sharedCalEnabled = Boolean(details.shared_calendar_enabled);
  const rawCal = details.calendar_info as
    | { provider_calendar_id?: string }
    | null
    | undefined;
  const hasCalendarInfo =
    sharedCalEnabled &&
    rawCal &&
    typeof rawCal === 'object' &&
    typeof rawCal.provider_calendar_id === 'string' &&
    rawCal.provider_calendar_id.length > 0;

  if (hasCalendarInfo) {
    mapped.calendar_info = details.calendar_info;
  }

  // Meeting rooms: build `meeting_rooms_attributes` from a 3-way
  // diff (current selection × server-side originals × plan-level
  // toggle):
  //
  //   - Plan disables meeting rooms → emit every server room with
  //     `_destroy: true`. (Mirrors legacy
  //     `useSchedulerDetailsPayload.tsx#501-505`.)
  //   - Email in selection but NOT on server → emit `{ email, name }`
  //     for create.
  //   - Email on server but NOT in selection → emit
  //     `{ id, _destroy: true }` for delete.
  //   - Email in BOTH → omit (already attached, no-op).
  //
  // Without the deletion branch the new wizard silently leaks rooms
  // on edit: a user removing a room from an existing schedule never
  // tells the backend, so calendar invites still land in the wrong
  // room. The audit that caught the `is_custom` gap also caught
  // this — see `docs/SELF_SCHEDULE_BEHAVIOR.md` G15. `_destroy` is
  // honored on both `/ats_interviews` and `/ats_schedules` per the
  // backend permit lists.
  const rawIds = details.meeting_room_ids;
  const currentEmails: string[] = Array.isArray(rawIds)
    ? rawIds.filter((v): v is string => typeof v === 'string' && v.length > 0)
    : typeof rawIds === 'string' && rawIds.length > 0
      ? [rawIds]
      : [];
  const originalRooms = Array.isArray(details._originalMeetingRooms)
    ? (details._originalMeetingRooms as Array<{
        id?: number | string;
        email?: string;
        name?: string;
      }>)
    : [];
  // Plan toggle defaults to `true` if missing — adhoc / unconfigured
  // plans behave as if rooms are enabled (matches legacy fallback).
  const meetingRoomsEnabled =
    'meeting_rooms_enabled' in details
      ? Boolean(details.meeting_rooms_enabled)
      : true;

  const meetingRoomEntries: Array<Record<string, unknown>> = [];

  if (!meetingRoomsEnabled) {
    for (const room of originalRooms) {
      if (room.id === undefined) continue;
      meetingRoomEntries.push({ id: room.id, _destroy: true });
    }
  } else {
    const originalEmails = new Set(
      originalRooms
        .map((r) => r.email)
        .filter((e): e is string => typeof e === 'string' && e.length > 0)
    );
    const currentEmailSet = new Set(currentEmails);
    // Additions.
    for (const email of currentEmails) {
      if (originalEmails.has(email)) continue;
      meetingRoomEntries.push({ email, name: email });
    }
    // Deletions.
    for (const room of originalRooms) {
      if (room.id === undefined) continue;
      if (typeof room.email === 'string' && currentEmailSet.has(room.email)) {
        continue;
      }
      meetingRoomEntries.push({ id: room.id, _destroy: true });
    }
  }

  if (meetingRoomEntries.length > 0) {
    mapped.meeting_rooms_attributes = meetingRoomEntries;
  }

  // Timezone — normalise to canonical IANA name before sending.
  if ('timezone' in details) {
    const raw = typeof details.timezone === 'string' ? details.timezone : '';
    mapped.timezone = raw ? normalizeTimeZoneForAtsCalendarApi(raw) : raw;
  }

  // Manual scheduling: extract start_time/end_time from bookedSlot.
  if (!isSelfSchedule) {
    const bookedSlot = details.bookedSlot as BookedSlot | null | undefined;
    if (bookedSlot?.start && bookedSlot?.end) {
      mapped.start_time = bookedSlot.start;
      mapped.end_time = bookedSlot.end;
    }
  }

  // Self-schedule: extract time_slots + schedule_type.
  if (isSelfSchedule) {
    const slots = details.selfScheduleSlots as ScheduleSlot[] | undefined;
    if (Array.isArray(slots) && slots.length > 0) {
      // Override classification per slot. Server-sourced slots that
      // the user hasn't moved (`_fromServer === true`) keep the
      // backend's classification verbatim — same source of truth, no
      // drift on round-trip. Everything else gets recomputed from the
      // current busy events + per-interviewer working hours via
      // `computeSlotOverride` (mirrors legacy
      // `useSchedulerDetailsPayload.tsx#294-329`). Falls back to
      // `none` when the override context is missing (panic-safe — the
      // wizard might lose the snapshot if the user hits Save before
      // CalendarPanel finishes its first emit).
      const overrideCtx = details._overrideContext as
        | SelfScheduleOverrideContext
        | undefined;
      mapped.time_slots = slots.map((slot) => {
        let is_custom: boolean;
        let override_type: SlotOverrideType;

        if (slot._fromServer) {
          is_custom = slot.is_custom ?? false;
          override_type = slot.override_type ?? 'none';
        } else if (overrideCtx) {
          const computed = computeSlotOverride({
            slotStart: slot.start,
            slotEnd: slot.end,
            busyEvents: [...overrideCtx.busyEvents],
            workingHoursByInterviewer: overrideCtx.workingHoursByInterviewer,
            interviewerIds: overrideCtx.interviewerIds,
            interviewKind: overrideCtx.interviewKind,
          });
          is_custom = computed.is_custom;
          override_type = computed.override_type;
        } else {
          is_custom = slot.is_custom ?? false;
          override_type = slot.override_type ?? 'none';
        }
        return {
          start_at: slot.start,
          end_at: slot.end,
          is_custom,
          override_type,
        };
      });
    }
    const findTimeValue = (details.findTimeValue ?? null) as FindTimeValue;
    mapped.schedule_type = scheduleTypeFor(findTimeValue);
    mapped.schedule_slots_modified = true;
    mapped.schedule_type_modified = true;

    if (typeof details.bufferBeforeMinutes === 'number') {
      mapped.buffer_before = details.bufferBeforeMinutes;
    }
    if (typeof details.bufferAfterMinutes === 'number') {
      mapped.buffer_after = details.bufferAfterMinutes;
    }
  }

  return mapped;
}
